import { Component, EventEmitter, Input, OnInit, Output } from '@angular/core';
import { FormArray, FormBuilder, FormGroup, Validators } from '@angular/forms';
import { DmcQuotesService } from 'src/app/business-service/dmc-quotes/dmc-quotes.service';
import { GlobalService } from 'src/app/modules/shared/services/global/global.service';
import { LovDataService } from 'src/app/modules/shared/services/lov-data/lov-data.service';
import { ResponseNotificationService } from 'src/app/modules/shared/services/response-notification/response-notification.service';
import { InputValidationsService } from 'src/app/modules/shared/services/validations/input-validations.service';

@Component({
  selector: 'app-dmc-quote-addon-tickets',
  templateUrl: './dmc-quote-addon-tickets.component.html',
  styleUrls: ['./dmc-quote-addon-tickets.component.scss'],
})
export class DmcQuoteAddonTicketsComponent implements OnInit {

  @Input() leadId: any;
  @Input() itineraryId: any;
  @Output() viewChange: EventEmitter<boolean> = new EventEmitter<boolean>();
  @Output() storeFormData: EventEmitter<any> = new EventEmitter();

  @Output() updateQuote: EventEmitter<void> = new EventEmitter();
  @Output() updateInventoryItinerary: EventEmitter<void> = new EventEmitter();

  @Input() itineraryDetails: any;
  @Input() storedFormData: any;
  @Input() editQuote: boolean = false;

  destinationsArray: any[] = [];

  sightSeeingForm!: FormGroup;

  constructor(
    private _globalService: GlobalService,
    private _fb: FormBuilder,
    private _responseNotificationService: ResponseNotificationService,
    private _responseService: ResponseNotificationService,
    private _lovDataService: LovDataService,
    private _validationService: InputValidationsService,
    private _dmcQuoteService: DmcQuotesService
  ) { }

  ngOnInit() {
    this.assignPackageDetailsData();
    this.sightSeeingForm = this._fb.group({
      itineraryId: [this.itineraryId],
      id: [null],
      organizationId: [null],
      ownership: [''],
      locations: this._fb.array([this.createLocationFormGroup()])
    });

    this.sightSeeingForm.valueChanges.subscribe(() => {
      this.sightSeeingForm.markAsTouched();
      this.sightSeeingForm.markAsDirty();
    })

    if (this.storedFormData) {
      this.patchSightSeeingPackage(this.storedFormData);
    }
  }

  assignPackageDetailsData() {
    this.destinationsArray = this.itineraryDetails.destination.map((item: any) => item.location)
  }
  createLocationFormGroup(): FormGroup {
    let data = this._fb.group({
      locationName: ['', [Validators.required]],
      visitingPlaces: this._fb.array([this.createVisitingPlacesFormGroup()])
    })
    return data;
  }

  createVisitingPlacesFormGroup(): FormGroup {
    let data = this._fb.group({
      place: ['', [Validators.required]],
      cost: [null, [Validators.required]],
      service: [true]
    })
    return data;
  }

  getLocationsArray(): FormArray {
    return this.sightSeeingForm.get('locations') as FormArray;
  }

  getVisitingPlacesArray(locationIndex: number): FormArray {
    return this.getLocationsArray().at(locationIndex).get('visitingPlaces') as FormArray;
  }

  addVisitingPlaces(locationIndex: number) {
    this.getVisitingPlacesArray(locationIndex).push(this.createVisitingPlacesFormGroup());
  }

  addLocation() {
    this.getLocationsArray().push(this.createLocationFormGroup());
  }

  removeVisitingPlaces(locationIndex: number, visitingPlaceIndex: number) {
    this.getVisitingPlacesArray(locationIndex).removeAt(visitingPlaceIndex);
  }

  removeAllVisitingPlaces(locationIndex: number) {
    this.getVisitingPlacesArray(locationIndex).clear();
    this.addVisitingPlaces(locationIndex);
  }

  removeLocation(locationIndex: number) {
    this.getLocationsArray().removeAt(locationIndex);
  }

  //  Location change funtion
  onLocationChange(event: any, locationIndex: number) {
    let value = event.target.value;
    this.getLocationsArray().at(locationIndex).get('locationName')?.setValue(value);
  }

  //  Patching data funtions
  patchSightSeeingPackage(formData: any) {
    const { locations, ...remainingData } = formData;
    this.sightSeeingForm.patchValue(remainingData);

    this.getLocationsArray().clear();
    locations.forEach((element: any, locationIndex: number) => {
      let locationGroup = this.createLocationFormGroup();
      locationGroup.patchValue({
        locationName: element.locationName,
      });
      this.getLocationsArray().push(locationGroup);
      this.patchVisitingPlaces(locationIndex, element.visitingPlaces);
    });

    this.sightSeeingForm.markAsPristine();
  }

  patchVisitingPlaces(locationIndex: number, visitingData: any) {
    const visitingPlacesArray = this.getVisitingPlacesArray(locationIndex);
    visitingPlacesArray.clear();
    visitingData.forEach((visitingPlace: any) => {
      let group = this.createVisitingPlacesFormGroup();
      group.patchValue(visitingPlace);
      visitingPlacesArray.push(group);
    });
  }



  //  Helper funtions
  createDestinationsFormArray(): any {
    if (this.itineraryDetails) {
      let data: FormGroup<any>[] = [];
      this.itineraryDetails.destination?.forEach((destination: any) => {
        let formGroup = this.createLocationFormGroup();
        formGroup.patchValue({
          locationName: destination.location,
        })
        data.push(formGroup);
      });
      return data;
    } else {
      this._responseNotificationService.showMessage("Package Details is not entered", true)
      return [];
    }
  }

  getAvailableLocations(locationIndex: number): string[] {
    const selectedLocations = this.getLocationsArray().controls
      .map(control => control.get('locationName')?.value)
      .filter(value => value); // Remove empty values

    return this.destinationsArray.filter(location => !selectedLocations.includes(location) || this.getLocationsArray().at(locationIndex).get('locationName')?.value === location);
  }

  async onSubmit(): Promise<boolean> {
    if (this.sightSeeingForm.valid) {
      let formData = await this.serializeFormData(this.sightSeeingForm.value);
      return this.handleLeadSpecificTickets(formData);
    } else {
      this.triggerInvalidForm();
      return false;
    }
  }

  triggerInvalidForm(){
    this.sightSeeingForm.markAllAsTouched();
    this._globalService.scrollToFirstInvalidControl();
    this._globalService.customAlert("Fill all required fields to save", "Invalid Form");
    return false;
  }

  private async handleLeadSpecificTickets(formData: any): Promise<boolean> {
    if (this.editQuote) {
      this.finalizeFormSubmission(formData);
      this.updateQuote.emit();
      return true;
    }

    try {
      const data = await this._dmcQuoteService.addAddOnTickets(formData).toPromise();
      this._responseNotificationService.showMessage("Tickets Pricing Added!");
      this.finalizeFormSubmission(data);
      return true;
    } catch (error) {
      console.error("Error adding Tickets pricing to lead", error);
      return false;
    }
  }

  private finalizeFormSubmission(data: any): void {
    this.sightSeeingForm.markAsPristine();
    data.dataSaved = true;
    this.storeFormData.emit(data);
  }

   serializeFormData(formData: any) {
    return formData;
  }

  async resetForm() {
    if (await this._globalService.confirmActionPopup("Confirm to reset data", "Confirm", "Confirm")) {
      this.ngOnInit();
    }
  }

  goPrevious() {
    this.viewChange.emit(true);
  }

  goNext() {
    this.viewChange.emit(false);
  }

  async saveAndNext() {
    if (await this.onSubmit()) {
      this.goNext()
    }
  }

  formDataModified(): boolean {
    return (this.sightSeeingForm.dirty || this.sightSeeingForm.invalid) && this.sightSeeingForm.touched;
  }

}
